// ФТУЕ.3-pre: пост-Pre-FTUE flow (всё в одном экране, без выхода на /home).
//
// Состоит из 5 фаз, переключаются по кнопке «Дальше»:
//   1. battle    — заставка боя (placeholder для будущего реального
//                  туториал-боя; сейчас 3 сек loading + кнопка «Победил»)
//   2. crate     — Контейнер Героев: 4 фракционные карточки появляются
//                  по очереди (flip-эффект). По одному Герою с каждой
//                  корпы.
//   3. corps     — Особенности корпораций: 4 карточки с описанием стиля
//                  игры. Чисто инфо, без выбора.
//   4. swipe     — Свайп-шоу мотивации: 4 слайда (Сигнал, БП, Лиги,
//                  Коллекционные карты / Маркет).
//                  Пагинатор внизу + auto-advance 4 сек или ручной тап.
//   5. finish    — Финал «Из учебки» + кнопка «В город» → setFtueState
//                  ('complete') + переход на /home.
//
// На сервере при complete выдаются награды (Контейнер Героев +
// 2 Осн.ШК + 1 Прод.ШК; если не было skip — доп. +1 Прод.ШК +
// 1 Осн.ШК + титул «Из учебки»). Серверная выдача — следующий этап.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '../telegram/useTelegram';
import { useTr } from '../i18n/useTr';
import { useGameStore } from '../store/useGameStore';
import { setFtueState, getProfile } from '../api/client';
import { TutorialBattle } from './TutorialBattle';
import {
  IcoTaunt, IcoStealth, IcoSilence, IcoInfected3, IcoImmune, IcoPierce
} from '../icons/keywordIcons';
import { ScriptsIcon } from '../components/ScriptsIcon';
import s from './TutorialBattleScreen.module.css';

// Хук «время на прочтение»: возвращает true только через delaySec
// секунд после mount или после смены resetKey. До этого момента
// кнопка «Дальше» заблокирована — игрок успевает прочесть.
function useReadingDelay(delaySec: number, resetKey: number | string = 0) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(false);
    const t = setTimeout(() => setReady(true), delaySec * 1000);
    return () => clearTimeout(t);
  }, [delaySec, resetKey]);
  return ready;
}

type Phase = 'battle' | 'crate' | 'keywords' | 'corps' | 'swipe' | 'finish';

// Keyword-ы карт. Информация по диздоку §6.4-6.5 и §6.7.
// SVG-иконки из app/src/icons/keywordIcons.tsx (порт из Claude Design).
// buildKeywords(t) — генератор с локализацией.
function buildKeywords(t: (ru: string, en: string) => string) {
  return [
    {
      Icon: IcoTaunt,
      title: t('ПРОВОКАЦИЯ', 'TAUNT'),
      body: t(
        'Других юнитов и Героя бить нельзя, пока есть юнит с Провокацией. Все авто-атаки юнитов противника пойдут в него. Тактика: ставь Провокацию на 1-ю линию, прячь хрупких на 2-ю.',
        "Other units and Hero can't be hit while a Taunt unit is on the field. All opponent auto-attacks go into it. Tactic: put Taunt on the 1st line, hide fragile units on the 2nd."
      ),
      accent: '#FFB13D'
    },
    {
      Icon: IcoStealth,
      title: t('СКРЫТНОСТЬ', 'STEALTH'),
      body: t(
        'Юнита со Скрытностью нельзя выбрать целью авто-атаки, Приказа или Действия — ни Героем, ни юнитами. Только AoE-карты могут его задеть. После его собственной атаки в Ф4/5 скрытность снимается.',
        "A Stealth unit can't be targeted by auto-attack, Order, or Action — neither by Hero nor units. Only AoE cards reach it. After its own attack in P4/5 stealth drops."
      ),
      accent: '#C93DFF'
    },
    {
      Icon: IcoSilence,
      title: t('МОЛЧАНИЕ', 'SILENCE'),
      body: t(
        'Снимает с цели ВСЁ: способности (Приказ/Аура/Триггер/Посмертие), кейворды, Модификации (Синтетикс), стаки Заражён (Лив Лайф). Статы откатываются к базовым. Спасает от опасных юнитов противника.',
        "Strips EVERYTHING from the target: abilities (Order/Aura/Trigger/Deathrattle), keywords, Mods (Synthetics), Infected stacks (Liv Life). Stats roll back to base. Saves you from dangerous enemy units."
      ),
      accent: '#3DF0FF'
    },
    {
      Icon: IcoInfected3,
      title: t('ЗАРАЖЁН', 'INFECTED'),
      body: t(
        'Фишка Лив Лайфа: цель получает 1 урон в начале каждого своего хода за каждый стак. Складывается до 3 раз — 1 стак = 1 урон, 2 = 2, 3 = 3. Снимается Молчанием. При смерти юнита не передаётся.',
        "Liv Life trick: target takes 1 damage at the start of each of its turns per stack. Stacks up to 3 — 1 stack = 1 damage, 2 = 2, 3 = 3. Removed by Silence. Doesn't transfer when the unit dies."
      ),
      accent: '#3DFFA5'
    },
    {
      Icon: IcoImmune,
      title: t('ИММУНИТЕТ', 'IMMUNITY'),
      body: t(
        'Юнит поглощает первый удар без потерь HP. После этого кейворд снимается — следующая атака уже считается. Хорош на ключевых юнитах против бурст-атак.',
        "Unit absorbs the first hit without losing HP. After that the keyword drops — next hit counts as normal. Good on key units against burst attacks."
      ),
      accent: '#9CFF3D'
    },
    {
      Icon: IcoPierce,
      title: t('ПРОБИТИЕ', 'PIERCE'),
      body: t(
        'Атака игнорирует Броню цели — урон идёт прямо в HP. Юниты с Пробитием в первую очередь нападают на бронированных. Если бронированных нет — бьют по правилу своей корпы.',
        "Attack ignores target Armor — damage goes straight to HP. Pierce units prefer armored targets first. If none are armored — they attack by their corp's rule."
      ),
      accent: '#FF3D3D'
    }
  ];
}

// Слайд может содержать JSX в `body` для иконок (Скрипты, ШК и т.д.).
type SwipeSlide = {
  icon: string;
  title: string;
  body: React.ReactNode;
};

const SI = () => (
  <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginInline: 2 }}>
    <ScriptsIcon size={14} />
  </span>
);

// buildSwipeSlides(t, isEn) — генератор с локализацией.
// JSX-слайды (с inline ScriptsIcon) собираются по флагу isEn чтобы не
// дублировать всю обёртку.
function buildSwipeSlides(t: (ru: string, en: string) => string, isEn: boolean): SwipeSlide[] {
  const signalBody = isEn ? (
    <>
      Invite a choom to Corps — earn Scripts (<SI />) with every of their Ranked win.
      The more active chooms you have, the more Scripts you get per win:
      from 1<SI /> to 3<SI />. Daily cap: 80<SI />.
      Your code is in Profile — tap and share with a choom.
    </>
  ) : (
    <>
      Пригласи чумбу в Корпы — получай Скрипты (<SI />) с каждой его победы
      в Рейтинговом бою. Чем больше активных чумб у тебя, тем больше Скриптов
      капает за одну победу: от 1<SI /> до 3<SI />. Дневной максимум: 80<SI />.
      Твой код — в Профиле, тапни и поделись с чумбой.
    </>
  );
  return [
    {
      icon: '📡',
      title: t('Сигнал', 'Beacon'),
      body: signalBody
    },
    {
      icon: '🎖',
      title: t('Боевой Пропуск', 'Battle Pass'),
      body: t(
        '50 уровней наград за сезон: карты, ШК, аватарки, титулы. За полное прохождение — наград примерно на $50–80.',
        '50 reward levels per season: cards, Cipher Crates, avatars, titles. Full completion = roughly $50–80 in rewards.'
      )
    },
    {
      icon: '🏆',
      title: t('Лиги · Сезоны', 'Leagues · Seasons'),
      body: t(
        'Поднимайся от Трущоб до Высшей лиги. За сезон: $2 если станешь Корпоратом, $10 — если попадёшь в Высшую лигу.',
        'Climb from the Slums to the Big League. Per season: $2 if you reach Corporates, $10 if you make it to the Big League.'
      )
    },
    {
      icon: '💰',
      title: t('Война Корпораций', 'Corp Wars'),
      body: t(
        'PvP-режим со ставками от $1 до $100. Между ходами можно поднять ставку — противник принимает блеф или отступает с потерями. Удвоение до ×4. Доступ с лиги Стритраннеров.',
        'PvP mode with bets from $1 to $100. Between turns you can raise the stake — opponent accepts the bluff or backs out with losses. Doubles up to ×4. Access from the Streetrunners league.'
      )
    },
    {
      icon: '🎁',
      title: t('Коллекционные карты', 'Collectible cards'),
      body: t(
        'Карты можно продавать и покупать на Маркете — за Скрипты или USDT. А ещё мы очень хотим, чтобы топовые карты однажды стали настоящими коллекционными подарками Telegram: носить их в профиле, дарить друзьям. Когда это случится — пока неизвестно, ждём подходящую технологию. Обычные NFT мы хорошим вариантом для игроков не считаем.',
        'Cards can be sold and bought on the Market — for Scripts or USDT. And we really want top cards to one day become real Telegram collectible gifts: wear them on your profile, gift them to friends. When this happens — unknown yet, we are waiting for the right technology. Plain NFTs we do not consider a good fit for players.'
      )
    }
  ];
}

// Кандидаты Героев на каждую фракцию. При открытии Контейнера Героев
// из каждого массива выбирается ОДИН случайный (как и выдаст сервер
// при complete-FTUE). Имена — из src/data/heroes-*.js.
interface HeroCard {
  id: string;
  short: string;     // короткое имя для лица карты (Танаки, Бишор, ...)
  full: string;      // полное имя для подписи
}
const FACTION_POOLS: Record<string, HeroCard[]> = {
  deus: [
    { id: 'deus_tanaki', short: 'Танаки', full: 'Генеральный директор' },
    { id: 'deus_kirosa', short: 'Кироса', full: 'Заместитель директора' },
    { id: 'deus_nikosi', short: 'Никоси', full: 'Старший оперативник' },
    { id: 'deus_sina',   short: 'Сина',   full: 'Руководитель внедрения' }
  ],
  syn: [
    { id: 'syn_bishor', short: 'Бишор', full: '«Мясник»' },
    { id: 'syn_rast',   short: 'Раст',   full: '«Грязный тактик»' },
    { id: 'syn_skvill', short: 'Сквилл', full: '«Умняк»' },
    { id: 'syn_uein',   short: 'Уэйн',   full: 'Рипер' }
  ],
  gsec: [
    { id: 'global_iv',     short: 'Ив',     full: 'Майор' },
    { id: 'global_li_pul', short: 'Ли Пул', full: 'Стратег' },
    { id: 'global_mit',    short: 'Мит',    full: 'Полковник' },
    { id: 'global_rayna',  short: 'Райна',  full: '«Дирижёр»' }
  ],
  liv: [
    { id: 'livlife_ariana', short: 'Ариана', full: 'Главврач' },
    { id: 'livlife_klod',   short: 'Клод',   full: 'Био-инженер' },
    { id: 'livlife_lisa',   short: 'Лиза',   full: 'Генетик' },
    { id: 'livlife_zayn',   short: 'Зейн',   full: 'Нейро-хирург' }
  ]
};

interface CrateFaction {
  factionId: string;
  flagArt: string;       // арт фракции (на back-стороне)
  hero: HeroCard;        // случайно выбранный Герой
  factionLabel: string;  // подпись «Деуса» / «Синтетикс» / ...
  accent: string;
}

function pickRandomHeroes(t: (ru: string, en: string) => string): CrateFaction[] {
  const pick = (arr: HeroCard[]) => arr[Math.floor(Math.random() * arr.length)];
  return [
    { factionId: 'deus', flagArt: '/img/factions/deus.jpg',    hero: pick(FACTION_POOLS.deus), factionLabel: t('Деуса', 'Deus'),     accent: '#8FB2FF' },
    { factionId: 'syn',  flagArt: '/img/factions/synt.jpg',    hero: pick(FACTION_POOLS.syn),  factionLabel: t('Синтетикс', 'Synthetics'), accent: '#C93DFF' },
    { factionId: 'gsec', flagArt: '/img/factions/g_sec.jpg',   hero: pick(FACTION_POOLS.gsec), factionLabel: t('Глобал', 'Global'),    accent: '#FFB13D' },
    { factionId: 'liv',  flagArt: '/img/factions/livlife.jpg', hero: pick(FACTION_POOLS.liv),  factionLabel: t('Лив Лайф', 'Liv Life'),  accent: '#3DFFA5' }
  ];
}

function buildCorpsInfo(t: (ru: string, en: string) => string) {
  return [
    {
      art: '/img/factions/deus.jpg',
      name: t('ДЕУС СИСТЕМС', 'DEUS SYSTEMS'),
      motto: t('«Доминация через Влияние»', '«Domination through Influence»'),
      play: t(
        'Хитрая тактика. Карты на поле помогают друг другу — чем больше ты выставил, тем сильнее они становятся. Игра в долгую, для тех кто любит думать.',
        "Cunning tactics. Cards on the field support each other — the more you've placed, the stronger they get. A long game for thinkers."
      ),
      accent: '#8FB2FF'
    },
    {
      art: '/img/factions/synt.jpg',
      name: t('СИНТЕТИКС СИНДИКАТ', 'SYNTHETICS SYNDICATE'),
      motto: t('«Киберпсихи»', '«Cyberpsychos»'),
      play: t(
        'Прокачка юнитов на лету. Накачиваешь свои карты апгрейдами — пара ходов и у тебя монстры. Но без защиты хрупкие.',
        "Upgrade units on the fly. Pump your cards with mods — a couple turns and you have monsters. But fragile without protection."
      ),
      accent: '#C93DFF'
    },
    {
      art: '/img/factions/g_sec.jpg',
      name: t('ГЛОБАЛ СЕКЬЮРИТИ', 'GLOBAL SECURITY'),
      motto: t('«Честь. Дисциплина.»', '«Honor. Discipline.»'),
      play: t(
        'Армия из дешёвых юнитов. Заполняешь поле, юниты стоят в строю и усиливают соседей. Простая и понятная корпа — хорошо для старта.',
        'An army of cheap units. Flood the field — units stand in formation and buff neighbors. Simple and clear corp — great for starters.'
      ),
      accent: '#FFB13D'
    },
    {
      art: '/img/factions/livlife.jpg',
      name: t('ЛИВ ЛАЙФ', 'LIV LIFE'),
      motto: t('«Выживай. Воскресай.»', '«Survive. Resurrect.»'),
      play: t(
        'Лечат своих и воскрешают убитых. Затягивают бой, давят терпением — пока враг устаёт, ты только набираешь силу.',
        'They heal their own and resurrect the fallen. Drag the fight, crush with patience — while the enemy tires, you only gain strength.'
      ),
      accent: '#3DFFA5'
    }
  ];
}

export function TutorialBattleScreen() {
  const navigate = useNavigate();
  const { hapticTap, hapticSuccess, hapticError } = useTelegram();
  const setFtueLocal = useGameStore(g => g.setFtueLocal);
  const syncFromServer = useGameStore(g => g.syncFromServer);
  const ftueStatus = useGameStore(g => g.ftueStatus);

  const [phase, setPhase] = useState<Phase>('battle');
  const [swipeIdx, setSwipeIdx] = useState(0);

  // Если игрок попал сюда без активного FTUE (например, открыл URL вручную) —
  // возвращаем на Home, чтобы не запутался.
  useEffect(() => {
    if (ftueStatus === 'completed' || ftueStatus === 'skipped') {
      navigate('/home', { replace: true });
    }
  }, [ftueStatus, navigate]);

  const finish = async () => {
    hapticSuccess();
    try {
      await setFtueState('complete');
      setFtueLocal('completed');
      try {
        const profile = await getProfile();
        syncFromServer(profile);
      } catch { /* offline */ }
      navigate('/home');
    } catch (e) {
      hapticError();
      console.error('[ftue] complete failed', e);
    }
  };

  return (
    <div className={s.host}>
      <div className={s.scanlines} />

      {phase === 'battle' && (
        <TutorialBattle onWin={() => { hapticTap(); setPhase('crate'); }} />
      )}

      {phase === 'crate' && (
        <CratePhase onNext={() => { hapticTap(); setPhase('keywords'); }} />
      )}

      {phase === 'keywords' && (
        <KeywordsPhase onNext={() => { hapticTap(); setPhase('corps'); }} />
      )}

      {phase === 'corps' && (
        <CorpsPhase onNext={() => { hapticTap(); setPhase('swipe'); setSwipeIdx(0); }} />
      )}

      {phase === 'swipe' && (
        <SwipePhase
          idx={swipeIdx}
          onSwipe={(next) => { hapticTap(); setSwipeIdx(next); }}
          onDone={() => { hapticTap(); setPhase('finish'); }}
        />
      )}

      {phase === 'finish' && (
        <FinishPhase onClose={finish} />
      )}
    </div>
  );
}

// Арт Героя по id с fallback на флаг фракции, если jpg ещё не залит.
function HeroArt({ heroId, fallback }: { heroId: string; fallback: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <img
      src={failed ? fallback : `/img/heroes/${heroId}.jpg`}
      alt=""
      onError={() => setFailed(true)}
    />
  );
}

// ============================================================
// Phase 2 · Crate (Контейнер Героев)
// ============================================================
function CratePhase({ onNext }: { onNext: () => void }) {
  const t = useTr();
  const [picks] = useState(() => pickRandomHeroes(t));
  const [revealed, setRevealed] = useState(0);

  useEffect(() => {
    if (revealed >= picks.length) return;
    const timer = setTimeout(() => setRevealed(x => x + 1), 1200);
    return () => clearTimeout(timer);
  }, [revealed, picks.length]);

  const allRevealed = revealed >= picks.length;

  return (
    <div className={s.screen}>
      <div className={s.kicker}>ftue · 06 / 10 · {t('награды', 'rewards')}</div>
      <h1 className={s.title}>{t('Твои', 'Your')} <em>{t('четверо', 'four')}</em></h1>
      <p className={s.subtitle}>{t('Каждой корпе — свой лидер. Случайный из четвёрки. Знакомься.', 'Every corp has its leader. Random of four. Meet them.')}</p>

      <div className={s.crateGrid}>
        {picks.map((p, i) => {
          const flipped = i < revealed;
          return (
            <div
              key={p.factionId}
              className={`${s.cflip} ${flipped ? s.cflipDone : ''}`}
              style={{ ['--c-accent' as any]: p.accent }}
            >
              <div className={s.cflipBack}>
                <img src={p.flagArt} alt={p.factionLabel} />
                <div className={s.cflipBackOverlay}>
                  <div className={s.cflipBackKicker}>{t('ЛИДЕР', 'LEADER')}</div>
                  <div className={s.cflipBackFaction}>{p.factionLabel.toUpperCase()}</div>
                </div>
              </div>
              <div className={s.cflipFront}>
                <HeroArt heroId={p.hero.id} fallback={p.flagArt} />
                <div className={s.cflipFrontOverlay}>
                  <div className={s.cflipFrontFull}>{p.hero.full}</div>
                  <div className={s.cflipFrontShort}>{p.hero.short}</div>
                  <div className={s.cflipFrontFaction}>{p.factionLabel}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className={s.foot}>
        <button
          className={s.btnPrimary}
          onClick={onNext}
          disabled={!allRevealed}
        >
          {allRevealed ? t('Дальше →', 'Next →') : `${revealed}/4 ${t('раскрыто', 'revealed')}`}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Phase 2.5 · Keywords карт (Провокация, Скрытность, Молчание, Заражён, Иммунитет)
// + блок про порядок авто-атак в Ф3 + упоминание режима изучения карт.
// ============================================================
function KeywordsPhase({ onNext }: { onNext: () => void }) {
  const t = useTr();
  const ready = useReadingDelay(3);
  const KEYWORDS_INFO = buildKeywords(t);
  return (
    <div className={s.screen}>
      <div className={s.kicker}>ftue · 07 / 10 · {t('способности', 'abilities')}</div>
      <h1 className={s.title}>{t('Способности', 'Card')} <em>{t('карт', 'Abilities')}</em></h1>
      <p className={s.subtitle}>
        {t(
          'У карт бывают спец-способности (кейворды). Эти 5 — основные, встретишь чаще всего:',
          'Cards can have special abilities (keywords). These 5 are the basics — most common:'
        )}
      </p>

      <div className={s.corpsList}>
        {KEYWORDS_INFO.map((kw) => {
          const Icon = kw.Icon;
          return (
            <div
              key={kw.title}
              className={s.corpsCard}
              style={{ ['--c-accent' as any]: kw.accent }}
            >
              <div className={s.kwIcon}>
                <Icon width={32} height={32} />
              </div>
              <div className={s.corpsInfo}>
                <div className={s.corpsName}>{kw.title}</div>
                <p className={s.corpsPlay}>{kw.body}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className={s.kwNote}>
        <div className={s.kwNoteTitle}>{t('⚙ Порядок авто-атак (Ф3)', '⚙ Auto-attack order (P3)')}</div>
        <p className={s.kwNoteBody}>
          {t(
            'Стороны бьют по очереди (zigzag): сначала «первая» сторона, потом противник, снова «первая», и т.д. Внутри стороны юниты атакуют в порядке: 1-я линия слева направо, потом 2-я линия слева направо. Если юнит уже отбился (контратаковал), в этой Ф3 его собственная атака пропускается — но он останется на поле и продолжит контратаковать новые удары.',
            "Sides take turns (zigzag): the «first» side, then opponent, «first» again, etc. Within a side units attack in order: 1st line left-to-right, then 2nd line left-to-right. If a unit already counter-attacked, its own attack in this P3 is skipped — but it stays on the field and keeps counter-attacking new hits."
          )}
        </p>
      </div>

      <div className={s.kwNote}>
        <div className={s.kwNoteTitle}>{t('📚 Детали карт', '📚 Card details')}</div>
        <p className={s.kwNoteBody}>
          {t(
            'У каждой карты — свои фишки и описания приказов. Чтобы изучить конкретную карту: на любом экране тапни «?» справа сверху — откроется режим изучения. Полная шпаргалка по кейвордам и фракционным механикам — там же.',
            'Each card has its own tricks and order descriptions. To study a specific card: tap «?» in the top-right of any screen — Study mode opens. Full cheatsheet on keywords and faction mechanics is there too.'
          )}
        </p>
      </div>

      <div className={s.foot}>
        <button
          className={s.btnPrimary}
          onClick={onNext}
          disabled={!ready}
        >
          {ready ? t('Дальше →', 'Next →') : t('читай...', 'reading...')}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Phase 3 · Особенности корпораций
// ============================================================
function CorpsPhase({ onNext }: { onNext: () => void }) {
  const t = useTr();
  const ready = useReadingDelay(3);
  const CORPS_INFO = buildCorpsInfo(t);
  return (
    <div className={s.screen}>
      <div className={s.kicker}>ftue · 07 / 10 · {t('корпы', 'corps')}</div>
      <h1 className={s.title}>{t('Стили', 'Corp')} <em>{t('корп', 'Styles')}</em></h1>
      <p className={s.subtitle}>
        {t(
          'Каждая корпа — свой подход к бою. Попробуй разные, найди свой.',
          'Every corp has its own approach to combat. Try them all — find yours.'
        )}
      </p>

      <div className={s.corpsList}>
        {CORPS_INFO.map((c) => (
          <div
            key={c.name}
            className={s.corpsCard}
            style={{ ['--c-accent' as any]: c.accent }}
          >
            <img src={c.art} className={s.corpsArt} alt={c.name} />
            <div className={s.corpsInfo}>
              <div className={s.corpsName}>{c.name}</div>
              <div className={s.corpsMotto}>{c.motto}</div>
              <p className={s.corpsPlay}>{c.play}</p>
            </div>
          </div>
        ))}
      </div>

      <div className={s.foot}>
        <button
          className={s.btnPrimary}
          onClick={onNext}
          disabled={!ready}
        >
          {ready ? t('Дальше →', 'Next →') : t('читай...', 'reading...')}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Phase 4 · Свайп-шоу мотивации
// ============================================================
function SwipePhase({
  idx, onSwipe, onDone
}: { idx: number; onSwipe: (next: number) => void; onDone: () => void }) {
  const t = useTr();
  const { i18n } = useTranslation();
  const slides = buildSwipeSlides(t, i18n.language === 'en');
  const slide = slides[idx];
  const ready = useReadingDelay(3, idx);

  const isLast = idx === slides.length - 1;
  const labelReady = isLast ? t('Финал →', 'Final →') : t('Дальше →', 'Next →');

  return (
    <div className={s.screen}>
      <div className={s.kicker}>ftue · {t('возможности', 'features')} · {idx + 1}/{slides.length}</div>

      <div className={s.swipeStage}>
        <div className={s.swipeIcon}>{slide.icon}</div>
        <h1 className={s.swipeTitle}>{slide.title}</h1>
        <p className={s.swipeBody}>{slide.body}</p>
      </div>

      <div className={s.swipeDots}>
        {slides.map((_, i) => (
          <span
            key={i}
            className={`${s.swipeDot} ${i === idx ? s.swipeDotActive : ''}`}
          />
        ))}
      </div>

      <div className={s.foot}>
        <button
          className={s.btnPrimary}
          onClick={() => isLast ? onDone() : onSwipe(idx + 1)}
          disabled={!ready}
        >
          {ready ? labelReady : t('читай...', 'reading...')}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Phase 5 · Финал
// ============================================================
function FinishPhase({ onClose }: { onClose: () => void }) {
  const t = useTr();
  const ready = useReadingDelay(3);
  return (
    <div className={s.screen}>
      <div className={s.kicker}>ftue · 10 / 10 · {t('финал', 'finale')}</div>

      <div className={s.finalTitleWrap}>
        <div className={s.finalStar}>★</div>
        <h1 className={s.finalTitle}>{t('«Из учебки»', '«Fresh Out the Boot»')}</h1>
        <div className={s.finalSubtitle}>{t('титул в твоём профиле', 'title in your profile')}</div>
      </div>

      <p className={s.subtitle} style={{ textAlign: 'center', marginTop: 18 }}>
        {t(
          'Ты прошёл обучение. На каждом экране есть значок ',
          "You're through the tutorial. Every screen has a "
        )}<b>?</b>{t(
          ' — он откроет режим изучения карт и подсказок. Если что — перезапуск туториала в Профиль → ⚙ → 🎓.',
          ' icon — opens Study mode for cards and hints. If needed — restart the tutorial in Profile → ⚙ → 🎓.'
        )}
      </p>

      <div className={s.foot}>
        <button
          className={s.btnPrimary}
          onClick={onClose}
          disabled={!ready}
        >
          {ready ? t('В ГОРОД, ЧУМБА →', 'HIT THE CITY, CHOOM →') : t('читай...', 'reading...')}
        </button>
      </div>
    </div>
  );
}
